import { createApi } from '@reduxjs/toolkit/query/react'

import { baseQueryWithReAuth } from './base-query'
import type { OkResponse } from './types/common'
import type {
	CreateRentalTaskBody,
	ListRentalTasksParams,
	ListRentalTasksResponse,
	RentalTask,
	UpdateRentalTaskBody,
} from './types/rental'

export const rentalTasksApi = createApi({
	reducerPath: 'rentalTasksApi',
	baseQuery: baseQueryWithReAuth,
	tagTypes: ['RentalTask'],
	endpoints: (builder) => ({
		listRentalTasks: builder.query<ListRentalTasksResponse, ListRentalTasksParams>({
			query: ({ rentalId, ...params }) => ({
				url: `/rent/${rentalId}/tasks`,
				params,
			}),
			providesTags: (_r, _e, { rentalId }) => [{ type: 'RentalTask', id: rentalId }],
		}),

		createRentalTask: builder.mutation<RentalTask | null, CreateRentalTaskBody>({
			query: ({ rentalId, ...body }) => ({
				url: `/rent/${rentalId}/tasks`,
				method: 'POST',
				body,
			}),
			invalidatesTags: (_r, _e, { rentalId }) => [{ type: 'RentalTask', id: rentalId }],
		}),

		updateRentalTask: builder.mutation<RentalTask | null, UpdateRentalTaskBody>({
			query: ({ rentalId, taskId, ...body }) => ({
				url: `/rent/${rentalId}/tasks/${taskId}`,
				method: 'PATCH',
				body,
			}),
			invalidatesTags: (_r, _e, { rentalId }) => [{ type: 'RentalTask', id: rentalId }],
		}),

		toggleRentalTask: builder.mutation<
			RentalTask | null,
			{ rentalId: string; taskId: string; isDone: boolean }
		>({
			query: ({ rentalId, taskId, isDone }) => ({
				url: `/rent/${rentalId}/tasks/${taskId}`,
				method: 'PATCH',
				body: { isDone },
			}),
			invalidatesTags: (_r, _e, { rentalId }) => [{ type: 'RentalTask', id: rentalId }],
		}),

		deleteRentalTask: builder.mutation<OkResponse, { rentalId: string; taskId: string }>({
			query: ({ rentalId, taskId }) => ({
				url: `/rent/${rentalId}/tasks/${taskId}`,
				method: 'DELETE',
			}),
			invalidatesTags: (_r, _e, { rentalId }) => [{ type: 'RentalTask', id: rentalId }],
		}),
	}),
})

export const {
	useListRentalTasksQuery,
	useCreateRentalTaskMutation,
	useUpdateRentalTaskMutation,
	useToggleRentalTaskMutation,
	useDeleteRentalTaskMutation,
} = rentalTasksApi
